import React, { useEffect, useState } from 'react';
import {
  getNavItems,
  createNavItem,
  updateNavItem,
  deleteNavItem,
} from '../../services/adminApi';
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
  HiOutlineArrowUp,
  HiOutlineArrowDown,
} from 'react-icons/hi';

interface NavItem {
  id: number;
  label: string;
  link: string;
  position: number;
  is_active: boolean;
}

const emptyForm = { label: '', link: '', position: 0, is_active: true };

const AdminNavigation: React.FC = () => {
  const [items, setItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const res = await getNavItems();
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      data.sort((a: NavItem, b: NavItem) => a.position - b.position);
      setItems(data);
    } catch {
      setError('Failed to load navigation items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, position: items.length });
    setShowModal(true);
    setError('');
  };

  const openEdit = (item: NavItem) => {
    setEditingId(item.id);
    setForm({ label: item.label, link: item.link, position: item.position, is_active: item.is_active });
    setShowModal(true);
    setError('');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this nav item?')) return;
    try {
      await deleteNavItem(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setMessage('Nav item deleted');
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setError('Failed to delete nav item');
    }
  };

  const moveItem = async (item: NavItem, direction: 'up' | 'down') => {
    const idx = items.findIndex((i) => i.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= items.length) return;

    const other = items[swapIdx];
    try {
      await Promise.all([
        updateNavItem(item.id, { ...item, position: other.position }),
        updateNavItem(other.id, { ...other, position: item.position }),
      ]);
      fetchData();
    } catch {
      setError('Failed to reorder');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await updateNavItem(editingId, form);
        setMessage('Nav item updated');
      } else {
        await createNavItem(form);
        setMessage('Nav item created');
      }
      setShowModal(false);
      fetchData();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const msgs = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
        setError(msgs);
      } else {
        setError('Failed to save nav item');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm({ ...form, [name]: checked });
    } else if (name === 'position') {
      setForm({ ...form, [name]: parseInt(value) || 0 });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  if (loading) return <div className="admin-spinner" />;

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Navigation</h1>
        <button className="admin-btn admin-btn-primary" onClick={openAdd}>
          <HiOutlinePlus size={18} /> Add Nav Item
        </button>
      </div>

      {message && <div className="admin-alert admin-alert-success">{message}</div>}
      {error && !showModal && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Label</th>
              <th>Link</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="admin-table-empty">No navigation items</td>
              </tr>
            ) : (
              items.map((item, idx) => (
                <tr key={item.id}>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-btn-icon"
                        onClick={() => moveItem(item, 'up')}
                        disabled={idx === 0}
                        title="Move up"
                      >
                        <HiOutlineArrowUp size={14} />
                      </button>
                      <button
                        className="admin-btn-icon"
                        onClick={() => moveItem(item, 'down')}
                        disabled={idx === items.length - 1}
                        title="Move down"
                      >
                        <HiOutlineArrowDown size={14} />
                      </button>
                    </div>
                  </td>
                  <td>{item.label}</td>
                  <td><code>{item.link}</code></td>
                  <td>
                    <span className={`admin-badge ${item.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-btn-icon" onClick={() => openEdit(item)} title="Edit">
                        <HiOutlinePencil size={16} />
                      </button>
                      <button className="admin-btn-icon admin-btn-icon-danger" onClick={() => handleDelete(item.id)} title="Delete">
                        <HiOutlineTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingId ? 'Edit Nav Item' : 'Add Nav Item'}</h2>
              <button className="admin-btn-icon" onClick={() => setShowModal(false)}>
                <HiOutlineX size={20} />
              </button>
            </div>
            {error && <div className="admin-alert admin-alert-error">{error}</div>}
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label>Label</label>
                <input name="label" value={form.label} onChange={handleChange} required />
              </div>
              <div className="admin-form-group">
                <label>Link</label>
                <input name="link" value={form.link} onChange={handleChange} required placeholder="/shop" />
              </div>
              <div className="admin-form-group">
                <label>Position</label>
                <input name="position" value={form.position} onChange={handleChange} type="number" />
              </div>
              <label className="admin-checkbox">
                <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                Active
              </label>
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNavigation;
